import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiURl from "../Paths/Api";

const StarBorder = ({
  as: Component = 'button',
  className = '',
  color = '#FFD700',
  speed = '3s',
  thickness = 1.5,
  children,
  ...rest
}) => {
  return (
    <Component
      className={`relative inline-block overflow-hidden rounded-full ${className} hover:scale-105 transition-transform duration-200`}
      style={{ padding: `${thickness}px 0`, ...rest.style }}
      {...rest}
    >
      <div
        className="absolute w-[300%] h-[50%] opacity-50 bottom-[-10px] right-[-250%] rounded-full animate-star-movement z-0"
        style={{ background: `radial-gradient(circle, ${color}, transparent 20%)`, animationDuration: speed }}
      ></div>
      <div
        className="absolute w-[300%] h-[50%] opacity-50 top-[-10px] left-[-250%] rounded-full animate-star-movement z-0"
        style={{ background: `radial-gradient(circle, ${color}, transparent 20%)`, animationDuration: speed }}
      ></div>
      <div className="relative z-1 bg-gradient-to-r from-purple-600 to-pink-500 border border-purple-700 text-white text-sm font-medium py-2 px-4 rounded-full shadow-sm hover:shadow-md transition-shadow duration-200">
        {children}
      </div>
    </Component>
  );
};

// Snowflake Component
const Snowflake = ({ style }) => (
  <div className="snowflake" style={style}>
    ❄
  </div>
);

// Floating WhatsApp Button Component
const FloatingWhatsAppButton = () => {
  const handleWhatsAppClick = () => {
    const phoneNumber = '+919654165454';
    const message = 'Hello! I need assistance with event booking.';
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <button
      onClick={handleWhatsAppClick}
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 animate-pulse-slow"
      aria-label="Contact on WhatsApp"
    >
      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.76.982.998-3.675-.236-.375a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.9 6.994c-.004 5.45-4.438 9.88-9.888 9.88m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.333.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.333 11.893-11.893 0-3.18-1.24-6.162-3.495-8.411" />
      </svg>
    </button>
  );
};

const PublicEvents = () => {
  const [meetings, setMeetings] = useState([]);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [pincode, setPincode] = useState('');
  const [address, setAddress] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [numTickets, setNumTickets] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [success, setSuccess] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [formMode, setFormMode] = useState('manual');
  const [isLoading, setIsLoading] = useState(false);
  const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('events'); // Default to events
  const [mosOption, setMosOption] = useState('no');
  const [snowflakes, setSnowflakes] = useState([]);
  const navigate = useNavigate();

  const RAZORPAY_KEY_ID = 'rzp_live_RKcrO8IhwM2Xj6';

  // Initialize snowflakes
  useEffect(() => {
    const flakes = [];
    for (let i = 0; i < 40; i++) {
      flakes.push({
        id: i,
        left: Math.random() * 100,
        animationDuration: `${Math.random() * 10 + 10}s`,
        animationDelay: `${Math.random() * 5}s`,
        opacity: Math.random() * 0.5 + 0.2,
        fontSize: `${Math.random() * 10 + 10}px`
      });
    }
    setSnowflakes(flakes);
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      setIsRazorpayLoaded(true);
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Razorpay Script Loaded`);
    };
    script.onerror = () => {
      setWarnings(['Payment gateway failed to load.']);
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Razorpay Script Error`);
    };
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    fetchPublicMeetings();
  }, []);

  const fetchPublicMeetings = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${ApiURl}/public-meetings.php`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const data = await response.json();
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Meetings API Response`, data);
      if (data.success && Array.isArray(data.meetings)) {
        const formattedMeetings = data.meetings.filter((meeting) => meeting.is_public == 1);
        setMeetings(formattedMeetings);
        
        // Always select the first meeting if available
        if (formattedMeetings.length > 0) {
          setSelectedMeetingId(formattedMeetings[0].id.toString());
        }
        
        // If only one event, open the form directly
        if (formattedMeetings.length === 1) {
          setActiveTab('purchase');
        } else {
          // If multiple events, show the events list
          setActiveTab('events');
        }
      } else {
        setWarnings([data.message || 'No events available.']);
        console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Meetings Error`, { error: data.message || 'Invalid response' });
      }
    } catch (err) {
      setWarnings(['Failed to fetch events.']);
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Meetings API Error`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleHomeClick = () => {
    navigate('/');
    console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Navigated to Home`);
  };

  const handleMeetingSelect = (meetingId) => {
    setSelectedMeetingId(meetingId.toString());
    setSuccess('');
    setWarnings([]);
    setName('');
    setEmail('');
    setPhone('');
    setPincode('');
    setAddress('');
    setCompanyName('');
    setNumTickets('');
    setCsvFile(null);
    setMosOption('no');
    setActiveTab('purchase');
    console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Meeting Selected`, { meetingId });
  };

  const validateInputs = () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim().replace(/^\+?91/, '');
    const trimmedPincode = pincode.trim();
    const trimmedAddress = address.trim();
    const trimmedCompanyName = companyName.trim();
    const ticketCount = parseInt(numTickets) || 0;

    if (!trimmedName || !trimmedEmail || !trimmedPhone || !trimmedPincode || !trimmedAddress || !numTickets) {
      return 'All required fields are mandatory.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      return 'Invalid email address.';
    }
    if (!/^\d{10}$/.test(trimmedPhone)) {
      return 'Invalid 10-digit phone number.';
    }
    if (!/^\d{6}$/.test(trimmedPincode)) {
      return 'Invalid 6-digit pincode.';
    }
    if (trimmedCompanyName && trimmedCompanyName.length > 255) {
      return 'Company name too long.';
    }
    if (ticketCount < 1) {
      return 'At least 1 ticket required.';
    }
    return '';
  };

  const storePendingAttendeeData = async () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim().replace(/^\+?91/, '');
    const trimmedPincode = pincode.trim();
    const trimmedAddress = address.trim();
    const trimmedCompanyName = companyName.trim();
    const ticketCount = parseInt(numTickets) || 0;

    const pendingData = {
      meetingId: selectedMeetingId,
      name: trimmedName,
      email: trimmedEmail,
      phone: trimmedPhone,
      pincode: trimmedPincode,
      address: trimmedAddress,
      companyName: trimmedCompanyName,
      numTickets: ticketCount,
    };

    try {
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Sending Pending Attendee Data`, pendingData);
      const response = await fetch(`${ApiURl}/store-attendee-data.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pendingData),
      });
      const data = await response.json();
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Pending Attendee Response`, data);
      if (!data.success) {
        console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Failed to store attendee data`, data.message);
      }
    } catch (err) {
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Attendee store error`, err);
    }
  };

  const handleJoinSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Join Submit`, {
      meetingId: selectedMeetingId,
      name,
      email,
      phone,
      pincode,
      address,
      companyName,
      numTickets,
      mosOption,
    });

    if (!selectedMeetingId) {
      setWarnings(['Select an event.']);
      return;
    }

    const selectedMeeting = meetings.find((m) => m.id.toString() === selectedMeetingId);
    if (!selectedMeeting) {
      setWarnings(['Event unavailable.']);
      return;
    }

    if (selectedMeeting.restricted == 1) {
      setWarnings(['This event requires booking through the counter. Please visit our ticket counter to book.']);
      return;
    }

    const validationError = validateInputs();
    if (validationError) {
      setWarnings([validationError]);
      return;
    }

    const currentAttendeeCount = selectedMeeting.attendee_count ?? (selectedMeeting.join_requests?.length || 0);
    const availableSpots = selectedMeeting.capacity - currentAttendeeCount;
    const ticketCount = parseInt(numTickets) || 0;

    if (availableSpots < ticketCount) {
      setWarnings([`Only ${availableSpots} ticket(s) available.`]);
      return;
    }

    setIsLoading(true);
    await storePendingAttendeeData();

    try {
      if (Number(selectedMeeting.price) === 0) {
        const joinData = {
          action: 'join',
          meetingId: selectedMeetingId,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim().startsWith('+91') ? phone.trim() : '+91' + phone.trim().replace(/^\+?91/, ''),
          pincode: pincode.trim(),
          address: address.trim(),
          companyName: companyName.trim(),
          numTickets: ticketCount,
        };
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Join Request (Free)`, joinData);
        const joinResponse = await fetch(`${ApiURl}/join-meeting.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(joinData),
        });
        const data = await joinResponse.json();
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Join Response (Free)`, data);
        if (data.success) {
          setSuccess('Tickets booked! Check your email.');
          setWarnings(data.warnings || []);
          setTimeout(() => {
            setSuccess('');
            setWarnings([]);
            setName('');
            setEmail('');
            setPhone('');
            setPincode('');
            setAddress('');
            setCompanyName('');
            setNumTickets('');
            setMosOption('no');
            fetchPublicMeetings();
          }, 3000);
        } else {
          setWarnings([data.message || 'Booking failed.']);
        }
      } else {
        if (!isRazorpayLoaded || !RAZORPAY_KEY_ID) {
          setWarnings(['Payment gateway unavailable.']);
          console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Razorpay Issue`, {
            isRazorpayLoaded,
            RAZORPAY_KEY_ID,
          });
          return;
        }

        const totalAmount = Number(selectedMeeting.price) * ticketCount;
        const orderResponse = await fetch(`${ApiURl}/join-meeting.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'create_order',
            amount: totalAmount * 100,
            currency: 'INR',
            receipt: `rcpt_${Date.now()}`,
          }),
        });
        const orderData = await orderResponse.json();
        console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Order Response`, orderData);
        if (!orderData.success || !orderData.id) {
          setWarnings([orderData.message || 'Order creation failed.']);
          console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Order Error`, orderData);
          return;
        }

        const options = {
          key: RAZORPAY_KEY_ID,
          amount: totalAmount * 100,
          currency: 'INR',
          name: selectedMeeting.company_name || 'Event Manager',
          description: `${ticketCount} ticket(s) for ${selectedMeeting.title || 'Event'}`,
          order_id: orderData.id,
          handler: async (response) => {
            try {
              if (!response.razorpay_payment_id || !response.razorpay_order_id) {
                setWarnings(['Invalid payment response.']);
                console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Invalid Razorpay Response`, response);
                return;
              }
              setIsPaymentProcessing(true);
              const joinData = {
                action: 'join',
                meetingId: selectedMeetingId,
                name: name.trim(),
                email: email.trim(),
                phone: phone.trim().startsWith('+91') ? phone.trim() : '+91' + phone.trim().replace(/^\+?91/, ''),
                pincode: pincode.trim(),
                address: address.trim(),
                companyName: companyName.trim(),
                numTickets: ticketCount,
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                signature: response.razorpay_signature,
              };
              console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Join Request`, joinData);
              const joinResponse = await fetch(`${ApiURl}/join-meeting.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(joinData),
              });
              const data = await joinResponse.json();
              console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Join Response`, data);
              if (data.success) {
                setSuccess('Tickets booked! Check your email.');
                setWarnings(data.warnings || []);
                setTimeout(() => {
                  setSuccess('');
                  setWarnings([]);
                  setName('');
                  setEmail('');
                  setPhone('');
                  setPincode('');
                  setAddress('');
                  setCompanyName('');
                  setNumTickets('');
                  setMosOption('no');
                  fetchPublicMeetings();
                }, 3000);
              } else {
                setWarnings([data.message || 'Booking failed.']);
              }
            } catch (err) {
              setWarnings(['Payment processing error.']);
              console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Handler Error`, err);
            } finally {
              setIsPaymentProcessing(false);
              setIsLoading(false);
            }
          },
          prefill: {
            name: name.trim(),
            email: email.trim(),
            contact: phone.trim().startsWith('+91') ? phone.trim() : '+91' + phone.trim().replace(/^\+?91/, ''),
          },
          theme: { color: '#6B46C1' },
          config: {
            display: {
              blocks: {
                upi: { name: 'UPI', instruments: [{ method: 'upi' }] },
                banks: { name: 'Netbanking', instruments: [{ method: 'netbanking' }] },
                cards: { name: 'Card', instruments: [{ method: 'card' }] },
              },
              sequence: ['upi', 'cards', 'banks'],
              preferences: { show_default_blocks: true },
            },
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (response) => {
          setWarnings(['Payment failed: ' + (response.error.description || 'Try again.')]);
          console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Payment Failed`, response.error);
          setIsLoading(false);
        });
        rzp.open();
      }
    } catch (err) {
      setWarnings(['Unexpected error. Try again.']);
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] Payment Init Error`, err);
    } finally {
      if (!isPaymentProcessing) setIsLoading(false);
    }
  };

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!selectedMeetingId || !csvFile) {
      setWarnings(['Select event and CSV file.']);
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] CSV Validation Error`, { error: 'Missing meeting or CSV' });
      return;
    }

    const selectedMeeting = meetings.find((m) => m.id.toString() === selectedMeetingId);
    if (!selectedMeeting || Number(selectedMeeting.price) > 0) {
      setWarnings(['CSV upload for free events only.']);
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] CSV Upload Error`, { error: 'Invalid meeting or paid event' });
      return;
    }

    if (selectedMeeting.restricted == 1) {
      setWarnings(['This event requires booking through the counter. Please visit our ticket counter to book.']);
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    formData.append('action', 'upload_csv');
    formData.append('meetingId', selectedMeetingId);

    try {
      const response = await fetch(`${ApiURl}/join-meeting.php`, { method: 'POST', body: formData });
      const data = await response.json();
      console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] CSV Upload Response`, data);
      if (data.success) {
        setSuccess('Tickets booked! Check your email.');
        setWarnings(data.errors || []);
        setTimeout(() => {
          setSuccess('');
          setWarnings([]);
          setCsvFile(null);
          fetchPublicMeetings();
        }, 3000);
      } else {
        setWarnings([data.message || 'CSV upload failed.']);
      }
    } catch (err) {
      setWarnings(['CSV upload error.']);
      console.error(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] CSV Upload Error`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = 'Name,Email,Phone,Pincode,Address,CompanyName\nJohn Doe,john@example.com,+1234567890,110001,123 Main St,Example Corp';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    console.log(`[${new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}] CSV Template Downloaded`);
  };

  const handleMosOptionChange = (option) => {
    setMosOption(option);
    if (option === 'yes') {
      setCompanyName('MOS');
    } else {
      setCompanyName('');
    }
  };

  const selectedMeeting = selectedMeetingId ? meetings.find((m) => m.id.toString() === selectedMeetingId) : null;
  const currentAttendeeCount = selectedMeeting ? (selectedMeeting.attendee_count ?? (selectedMeeting.join_requests?.length || 0)) : 0;
  const availableSpots = selectedMeeting ? selectedMeeting.capacity - currentAttendeeCount : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50 to-blue-50 font-sans overflow-hidden relative">
      {/* Snow Background */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        {snowflakes.map((flake) => (
          <Snowflake
            key={flake.id}
            style={{
              position: 'absolute',
              left: `${flake.left}%`,
              top: '-20px',
              fontSize: flake.fontSize,
              opacity: flake.opacity,
              animation: `fall ${flake.animationDuration} linear infinite`,
              animationDelay: flake.animationDelay,
              userSelect: 'none',
            }}
          />
        ))}
      </div>

      {(isLoading || isPaymentProcessing) && (
        <div className="fixed inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-2xl shadow-2xl flex flex-col items-center border border-purple-200">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-600 mb-4"></div>
            <p className="text-purple-800 text-lg font-bold">
              {isPaymentProcessing ? 'Confirming your tickets...' : 'Processing your request...'}
            </p>
            <p className="text-purple-600 text-sm mt-2">Please wait while we secure your booking</p>
          </div>
        </div>
      )}

      <header className="bg-white/80 backdrop-blur-md shadow-lg sticky top-0 z-40 border-b border-purple-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Event Manager</h1>
              <p className="text-xs text-gray-500">Book your tickets seamlessly</p>
            </div>
          </div>
          <button
            onClick={handleHomeClick}
            className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl text-sm font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 shadow-md"
            disabled={isLoading || isPaymentProcessing}
          >
            ← Back Home
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 relative z-10">
        <div className="bg-gradient-to-br from-white/90 to-purple-50/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-purple-100 overflow-hidden">
          {/* Always show tabs for both single and multiple events */}
          <div className="flex border-b border-purple-100">
            <button
              className={`flex-1 py-4 text-base font-bold transition-all duration-300 ${activeTab === 'events' ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border-b-4 border-purple-600' : 'bg-white/50 text-gray-600 hover:bg-purple-50/70'}`}
              onClick={() => setActiveTab('events')}
            >
              🎯 Browse Events ({meetings.length})
            </button>
            <button
              className={`flex-1 py-4 text-base font-bold transition-all duration-300 ${activeTab === 'purchase' ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border-b-4 border-purple-600' : 'bg-white/50 text-gray-600 hover:bg-purple-50/70'}`}
              onClick={() => setActiveTab('purchase')}
              disabled={!selectedMeeting}
            >
              🎫 {selectedMeeting?.company_name || 'Book Tickets'}
            </button>
          </div>

          <div className="p-6">
            {activeTab === 'events' && (
              <div>
                <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-800 to-pink-700 bg-clip-text text-transparent mb-8">✨ Upcoming Events ({meetings.length})</h2>

                {meetings.length === 0 && (
                  <div className="text-center py-12 bg-gradient-to-r from-purple-50/80 to-pink-50/80 rounded-2xl border-2 border-dashed border-purple-200 animate-fade-in">
                    <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-xl text-purple-700 font-bold">No events available at the moment</p>
                    <p className="text-gray-500 mt-2">Check back soon for exciting events!</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {meetings.map((meeting, index) => (
                    <div
                      key={meeting.id}
                      className={`relative cursor-pointer rounded-2xl bg-gradient-to-br from-white to-purple-50/50 shadow-xl hover:shadow-2xl transition-all duration-500 border-2 ${
                        selectedMeetingId === meeting.id.toString() ? 'border-purple-500 ring-4 ring-purple-300/50' : 'border-purple-100'
                      } transform hover:-translate-y-2 animate-fade-in overflow-hidden group`}
                      style={{ animationDelay: `${index * 0.05}s` }}
                      onClick={() => !isLoading && !isPaymentProcessing && handleMeetingSelect(meeting.id)}
                    >
                      <div className="absolute top-4 right-4 z-10">
                        <div className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-bold rounded-full shadow-lg">
                          ₹{meeting.price || 0}
                        </div>
                        {meeting.restricted == 1 && (
                          <div className="mt-2 px-3 py-1.5 bg-gradient-to-r from-red-600 to-red-500 text-white text-xs font-bold rounded-full shadow-lg">
                            🏪 Counter Only
                          </div>
                        )}
                      </div>

                      <div className="relative w-full h-48 overflow-hidden rounded-t-2xl">
                        {meeting.image_url ? (
                          <img
                            src={`${ApiURl}/${meeting.image_url}`}
                            alt={meeting.title || 'Event'}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-r from-purple-300/30 to-pink-300/30 flex items-center justify-center">
                            <div className="w-16 h-16 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                      </div>

                      <div className="p-6">
                        <h3 className="font-bold text-lg text-purple-900 line-clamp-1 mb-3 group-hover:text-purple-700 transition-colors">
                          {meeting.title || 'Untitled Event'}
                        </h3>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                            <span className="line-clamp-1">{meeting.location || 'Online'}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="line-clamp-1">
                              {meeting.time && !isNaN(new Date(meeting.time).getTime())
                                ? new Date(meeting.time).toLocaleString('en-US', {
                                    timeZone: 'Asia/Kolkata',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : 'TBA'}
                            </span>
                          </div>
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t border-purple-100">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                              {meeting.company_name?.charAt(0) || 'E'}
                            </div>
                            <span className="text-sm font-medium text-gray-700 line-clamp-1">
                              {meeting.company_name || 'Event Manager'}
                            </span>
                          </div>
                          <span className={`px-3 py-1.5 text-xs font-bold rounded-full ${
                            availableSpots > 10 ? 'bg-gradient-to-r from-green-100 to-green-50 text-green-700' :
                            availableSpots > 0 ? 'bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-700' :
                            'bg-gradient-to-r from-red-100 to-red-50 text-red-700'
                          }`}>
                            {availableSpots} spots
                          </span>
                        </div>
                      </div>

                      <div className="px-6 pb-6">
                        <StarBorder
                          className="w-full text-sm"
                          color="#FFD700"
                          speed="2s"
                          thickness={1.5}
                          onClick={(e) => {
                            e.stopPropagation();
                            !isLoading && !isPaymentProcessing && handleMeetingSelect(meeting.id);
                          }}
                          disabled={isLoading || isPaymentProcessing}
                        >
                          {selectedMeetingId === meeting.id.toString() ? '✓ Selected' : 'Select Event'}
                        </StarBorder>
                      </div>
                    </div>
                  ))}
                </div>

                {selectedMeeting && (
                  <div className="mt-8 p-6 bg-gradient-to-r from-purple-50/80 to-pink-50/80 rounded-2xl border-2 border-purple-200 shadow-lg animate-fade-in">
                    <div className="flex flex-col lg:flex-row items-center justify-between">
                      <div className="flex items-center mb-4 lg:mb-0">
                        <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg mr-4">
                          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-bold text-xl text-purple-800">{selectedMeeting.title}</h3>
                          <div className="flex items-center mt-1">
                            <span className="text-sm text-purple-600 font-medium">{availableSpots} spots available</span>
                            <span className="mx-2 text-gray-400">•</span>
                            <span className="text-sm text-gray-600">{selectedMeeting.location || 'Online'}</span>
                          </div>
                        </div>
                      </div>
                      <StarBorder
                        className="px-8 py-3 text-base"
                        color="#FFD700"
                        speed="2s"
                        thickness={1.5}
                        onClick={() => setActiveTab('purchase')}
                      >
                        🎫 Book Now
                      </StarBorder>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'purchase' && selectedMeeting && (
              <div className="animate-fade-in">
                <div className="flex items-center mb-8">
                  <button
                    onClick={() => setActiveTab('events')}
                    className="p-3 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl hover:shadow-lg transition-all duration-300 mr-4"
                  >
                    <svg className="w-5 h-5 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </button>
                  <div>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-800 to-pink-700 bg-clip-text text-transparent">
                      Book Your Tickets
                    </h2>
                    <p className="text-gray-600 mt-1">Secure your spot for {selectedMeeting.company_name || 'this amazing event'}</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50/80 to-pink-50/80 backdrop-blur-sm border-2 border-purple-200 rounded-2xl p-6 mb-8 shadow-lg">
                  <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between">
                    <div className="mb-6 lg:mb-0">
                      <h3 className="font-bold text-2xl text-purple-800 mb-2">{selectedMeeting.title || 'Untitled Event'}</h3>
                      <div className="flex flex-wrap items-center text-sm text-gray-700 gap-4">
                        <div className="flex items-center bg-white/50 px-3 py-1.5 rounded-lg">
                          <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          {selectedMeeting.location || 'Online'}
                        </div>
                        <div className="flex items-center bg-white/50 px-3 py-1.5 rounded-lg">
                          <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {selectedMeeting.time && !isNaN(new Date(selectedMeeting.time).getTime())
                            ? new Date(selectedMeeting.time).toLocaleString('en-US', {
                                timeZone: 'Asia/Kolkata',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'TBA'}
                        </div>
                        <div className="flex items-center bg-white/50 px-3 py-1.5 rounded-lg">
                          <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                          </svg>
                          <span className={availableSpots > 10 ? 'text-green-600 font-bold' : availableSpots > 0 ? 'text-yellow-600 font-bold' : 'text-red-600 font-bold'}>
                            {availableSpots} spots left
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-purple-800">
                        ₹{selectedMeeting.price || 0} <span className="text-base font-normal text-gray-600">/ticket</span>
                      </div>
                      {selectedMeeting.company_name && (
                        <div className="text-sm text-gray-600 mt-1">Organized by {selectedMeeting.company_name}</div>
                      )}
                      {selectedMeeting.restricted == 1 && (
                        <div className="text-sm text-red-600 font-bold mt-2 flex items-center justify-end">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          Counter Booking Only
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {success && (
                  <div className="mb-8 p-5 bg-gradient-to-r from-green-50 to-emerald-50 text-green-800 rounded-2xl text-base animate-fade-in border-2 border-green-200 shadow-lg">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-bold text-lg">🎉 {success}</p>
                        <p className="text-sm text-green-600 mt-1">Check your email and WhatsApp for ticket confirmation</p>
                      </div>
                    </div>
                  </div>
                )}

                {warnings.length > 0 && (
                  <div className="mb-8 p-5 bg-gradient-to-r from-yellow-50 to-orange-50 text-yellow-800 rounded-2xl text-base animate-fade-in border-2 border-yellow-200 shadow-lg">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <span className="font-bold text-lg">Important Notice</span>
                    </div>
                    <ul className="list-disc pl-5 text-sm">
                      {warnings.map((warning, index) => (
                        <li key={index} className="mb-1">{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedMeeting.restricted == 1 ? (
                  <div className="text-center p-8 bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl border-2 border-red-200 shadow-lg">
                    <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <p className="text-xl text-red-700 font-bold mb-2">Counter Booking Required</p>
                    <p className="text-gray-600">Please visit our physical ticket counter to book tickets for this event.</p>
                  </div>
                ) : (
                  <>
                    <div className="border-b border-purple-200 mb-8">
                      <div className="flex space-x-2">
                        <button
                          className={`px-8 py-3 text-base font-bold rounded-t-2xl transition-all duration-300 ${formMode === 'manual' ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-lg' : 'bg-gradient-to-r from-purple-100 to-pink-100 text-gray-600 hover:bg-purple-200'}`}
                          onClick={() => setFormMode('manual')}
                          disabled={isLoading || isPaymentProcessing}
                        >
                          ✏️ Manual Booking
                        </button>
                        {/* <button
                          className={`px-8 py-3 text-base font-bold rounded-t-2xl transition-all duration-300 ${formMode === 'csv' ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-lg' : 'bg-gradient-to-r from-purple-100 to-pink-100 text-gray-600 hover:bg-purple-200'}`}
                          onClick={() => setFormMode('csv')}
                          disabled={isLoading || isPaymentProcessing || Number(selectedMeeting.price) > 0}
                        >
                          📁 Bulk CSV Upload
                        </button> */}
                      </div>
                    </div>

                    {formMode === 'manual' && (
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="block text-base font-bold text-purple-800">
                              <span className="flex items-center">
                                👤 Name <span className="text-red-500 ml-1">*</span>
                              </span>
                            </label>
                            <input
                              type="text"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              placeholder="Enter your full name"
                              className="w-full px-5 py-3.5 bg-white/80 backdrop-blur-sm border-2 border-purple-200 rounded-xl text-base focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-sm transition-all duration-300"
                              required
                              disabled={isLoading || isPaymentProcessing}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-base font-bold text-purple-800">
                              <span className="flex items-center">
                                📧 Email <span className="text-red-500 ml-1">*</span>
                              </span>
                            </label>
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="you@example.com"
                              className="w-full px-5 py-3.5 bg-white/80 backdrop-blur-sm border-2 border-purple-200 rounded-xl text-base focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-sm transition-all duration-300"
                              required
                              disabled={isLoading || isPaymentProcessing}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-base font-bold text-purple-800">
                              <span className="flex items-center">
                                📱 Phone <span className="text-red-500 ml-1">*</span>
                              </span>
                            </label>
                            <input
                              type="tel"
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              placeholder="10-digit phone number"
                              className="w-full px-5 py-3.5 bg-white/80 backdrop-blur-sm border-2 border-purple-200 rounded-xl text-base focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-sm transition-all duration-300"
                              required
                              disabled={isLoading || isPaymentProcessing}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-base font-bold text-purple-800">
                              <span className="flex items-center">
                                📍 Pincode <span className="text-red-500 ml-1">*</span>
                              </span>
                            </label>
                            <input
                              type="text"
                              value={pincode}
                              onChange={(e) => setPincode(e.target.value)}
                              placeholder="6-digit pincode"
                              className="w-full px-5 py-3.5 bg-white/80 backdrop-blur-sm border-2 border-purple-200 rounded-xl text-base focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-sm transition-all duration-300"
                              required
                              disabled={isLoading || isPaymentProcessing}
                            />
                          </div>
                          <div className="md:col-span-2 space-y-2">
                            <label className="block text-base font-bold text-purple-800">
                              <span className="flex items-center">
                                🏠 Address <span className="text-red-500 ml-1">*</span>
                              </span>
                            </label>
                            <input
                              type="text"
                              value={address}
                              onChange={(e) => setAddress(e.target.value)}
                              placeholder="Your complete address"
                              className="w-full px-5 py-3.5 bg-white/80 backdrop-blur-sm border-2 border-purple-200 rounded-xl text-base focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-sm transition-all duration-300"
                              required
                              disabled={isLoading || isPaymentProcessing}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-base font-bold text-purple-800">
                              <span className="flex items-center">
                                🎫 Tickets <span className="text-red-500 ml-1">*</span>
                              </span>
                            </label>
                            <input
                              type="number"
                              min="1"
                              max={availableSpots}
                              value={numTickets}
                              onChange={(e) => {
                                const value = e.target.value;
                                setNumTickets(value === '' ? '' : Math.min(availableSpots, parseInt(value) || 1).toString());
                              }}
                              placeholder="Number of tickets"
                              className="w-full px-5 py-3.5 bg-white/80 backdrop-blur-sm border-2 border-purple-200 rounded-xl text-base focus:ring-2 focus:ring-purple-500 focus:border-purple-500 shadow-sm transition-all duration-300 [appearance:textfield]"
                              required
                              disabled={isLoading || isPaymentProcessing}
                            />
                            <p className="text-sm text-purple-600 font-medium">
                              🏷️ {availableSpots} tickets available | {selectedMeeting.price === 0 ? 'FREE' : `₹${selectedMeeting.price} each`}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-base font-bold text-purple-800">
                              <span className="flex items-center">
                                🏢 MOS <span className="text-red-500 ml-1">*</span>
                              </span>
                            </label>
                            <div className="flex space-x-6">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  name="mosOption"
                                  value="yes"
                                  checked={mosOption === 'yes'}
                                  onChange={(e) => handleMosOptionChange(e.target.value)}
                                  className="h-5 w-5 text-purple-600 border-purple-300 focus:ring-2 focus:ring-purple-500"
                                  disabled={isLoading || isPaymentProcessing}
                                />
                                <span className="ml-3 text-base font-medium text-gray-700">✅ Yes</span>
                              </label>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  name="mosOption"
                                  value="no"
                                  checked={mosOption === 'no'}
                                  onChange={(e) => handleMosOptionChange(e.target.value)}
                                  className="h-5 w-5 text-purple-600 border-purple-300 focus:ring-2 focus:ring-purple-500"
                                  disabled={isLoading || isPaymentProcessing}
                                />
                                <span className="ml-3 text-base font-medium text-gray-700">❌ No</span>
                              </label>
                            </div>
                            {mosOption === 'yes' && (
                              <div className="mt-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl">
                                <p className="text-sm font-bold text-purple-700">🏢 Company set to: "MOS"</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-r from-purple-50/80 to-pink-50/80 rounded-2xl p-6 border-2 border-purple-200">
                          <div className="flex flex-col md:flex-row items-center justify-between">
                            <div>
                              <p className="text-lg font-bold text-purple-800 mb-1">Order Summary</p>
                              <p className="text-gray-600">
                                {numTickets || 0} ticket{numTickets != 1 ? 's' : ''} × ₹{selectedMeeting.price || 0}
                              </p>
                            </div>
                            <div className="mt-4 md:mt-0">
                              <p className="text-3xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
                                ₹{selectedMeeting ? selectedMeeting.price * (parseInt(numTickets) || 0) : 0}
                              </p>
                              <p className="text-sm text-gray-500 text-right">Total Amount</p>
                            </div>
                          </div>
                        </div>

                        <StarBorder
                          className="w-full text-lg py-4"
                          color="#FFD700"
                          speed="2s"
                          thickness={2}
                          onClick={handleJoinSubmit}
                          disabled={isLoading || isPaymentProcessing || availableSpots < 1}
                        >
                          {isLoading || isPaymentProcessing ? (
                            <span className="flex items-center justify-center">
                              <svg className="animate-spin h-5 w-5 mr-2 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Processing...
                            </span>
                          ) : availableSpots < 1 ? 'Sold Out' : selectedMeeting && (selectedMeeting.price === 0 || selectedMeeting.price === '0') ? `🎟️ Book ${numTickets || 0} Ticket${numTickets != 1 ? 's' : ''}` : `💳 Pay ₹${selectedMeeting.price * (parseInt(numTickets) || 0)}`}
                        </StarBorder>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Floating WhatsApp Button */}
      <FloatingWhatsAppButton />

      <style jsx>{`
        @keyframes star-movement {
          0% { transform: translate(0%, 0%); opacity: 0.6; }
          100% { transform: translate(-100%, 0%); opacity: 0; }
        }
        @keyframes fade-in {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
          10% { opacity: 0.7; }
          90% { opacity: 0.7; }
          100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
        }
        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .animate-star-movement {
          animation: star-movement linear infinite alternate;
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        .animate-pulse-slow {
          animation: pulse-slow 2s infinite;
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .snowflake {
          pointer-events: none;
          user-select: none;
        }
      `}</style>
    </div>
  );
};

export default PublicEvents;